// @flow
import * as React from 'react';
import { FIELDS_SET_FROM_MODEL, MODEL_FIELD_NAME } from 'esm-web-pro/containers/pages/network/Util';
import { APIContext } from 'speed-js-react';
import ApiForm from 'esm-web-pro/containers/forms/ApiForm';
import MapObjectForm from 'esm-web-pro/containers/map/forms/MapObjectForm';
import MapObjectFunctionalForm from 'esm-web-pro/containers/map/forms/MapObjectFunctionalForm';
import { Formik } from 'formik';

type Props = {
    objectType: string | null,
    // eslint-disable-next-line react/require-default-props
    onLoadModelData?: (modelData: Object) => void,
    // eslint-disable-next-line react/require-default-props
    onChange?: Function,
    // eslint-disable-next-line react/require-default-props
    forwardRef?: React.ElementRef<Formik>,
    // eslint-disable-next-line react/require-default-props
    initial: Object
}

const withModel = (WrappedForm) => ({
    forwardRef = null, onChange = () => {}, initial = undefined, onLoadModelData = () => {}, ...props
}: Props) => {
    /**
     * Form which wraps ApiForm or MapObjectForm and sets the values of the fields
     * which are based on the model of the network object, e.g. the fiber_count and tube_count of the Cable,
     * height of the ODF and the export fields
     */
    const formRef = forwardRef || React.useRef(null);
    const isInitialMount = React.useRef(true);
    const {
        objectType,
        ...rest
    } = props;

    const { apiClient } = React.useContext(APIContext);

    const { excludeFields } = rest;

    const [model, setModel] = React.useState(initial && initial[MODEL_FIELD_NAME[objectType]]
        ? initial[MODEL_FIELD_NAME[objectType]].code : null);

    const handleChange = async (event) => {
        if (formRef && formRef.current && objectType) {
            await formRef.current.getFormikBag().validateForm();

            if (formRef.current) {
                const { values } = formRef.current.getFormikBag();
                const modelValue = values[MODEL_FIELD_NAME[objectType]];

                if (typeof modelValue === 'object' && modelValue !== null) {
                    setModel(modelValue.code);
                } else {
                    setModel(modelValue);
                }
            }
        }
        onChange(event);
    };

    const setModelData = async () => {
        const { data } = await apiClient.resources.nomenclatureItem.get(model);
        onLoadModelData(data);
        if (Object.prototype.hasOwnProperty.call(FIELDS_SET_FROM_MODEL, objectType)) {
            Object.entries(FIELDS_SET_FROM_MODEL[objectType])
                .forEach(([key, value]) => {
                    if (!excludeFields || !excludeFields.includes(key)) {
                        formRef.current.getFormikBag().setFieldValue(key, data[value]);
                    }
                });
        }
    };

    const resetModelData = () => {
        if (objectType && formRef && formRef.current) {
            if (Object.prototype.hasOwnProperty.call(FIELDS_SET_FROM_MODEL, objectType)) {
                Object.keys(FIELDS_SET_FROM_MODEL[objectType])
                    .forEach((key) => {
                        if (!excludeFields || !excludeFields.includes(key)) {
                            const formikBag = formRef.current.getFormikBag();
                            const { initialValues: formInitialValues } = formikBag;
                            formRef.current.getFormikBag().setFieldValue(key, formInitialValues[key]);
                        }
                    });
            }
        }
    };

    React.useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        if (!isInitialMount.current) {
            if (model) {
                setModelData();
            } else {
                resetModelData();
            }
        }
    }, [model]);

    return <WrappedForm forwardRef={formRef} onChange={handleChange} initial={initial} {...rest} />;
};

export const ApiFormWithModel = withModel(ApiForm);
export const MapObjectFormWithModel = withModel(MapObjectFunctionalForm);
